import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { cor, texto } from '#components/finapp/estilo';
import type { Escopo } from '#components/finapp/motor/escopo';
import { useLocale } from '#hooks/useLocale';

type Props = {
  mes: string;
  onMudarMes: (mes: string) => void;
  escopo: Escopo;
  onMudarEscopo: (escopo: Escopo) => void;
  ultimoDia: string | null;
  /**
   * Celular. Vem de `useResponsive()` em `Inicio.tsx`.
   *
   * ⚠️ Os três controles (mês · escopo · data do lançamento) não cabem lado a
   * lado em 390px: eles se sobrepõem, e o texto de um fica por cima do outro.
   */
  estreito?: boolean;
};

/**
 * ⚠️⚠️ A frase do lado direito NÃO PODE prometer atualidade de dado.
 *
 * Este fork não tem sincronização automática (Open Finance/Pluggy está fora
 * do escopo do plano — ver `estudos/app-financeiro-fork-actual-2026-09-05.md`
 * §8.7). O app anterior do dono tinha um botão "Atualizar" puxando o Open
 * Finance, e "extrato até DD/MM" era, ali, literalmente "sincronizado até
 * DD/MM". Repetir essa frase aqui, onde não existe sync nenhum, faria ele
 * ler "extrato até 05/09" como "já sincronizei hoje" e confiar num número
 * que só reflete o que foi LANÇADO (à mão ou por importação), não o que
 * aconteceu de fato no banco. Por isso a frase fala do LANÇAMENTO mais
 * recente, nunca de "extrato" ou "atualização".
 */
function textoDoLancamento(
  t: (key: string, options?: Record<string, unknown>) => string,
  ultimoDia: string | null,
  locale: ReturnType<typeof useLocale>,
): string {
  if (ultimoDia === null) {
    return t('nenhum lançamento neste mês');
  }
  return t('último lançamento em {{dia}}', {
    dia: monthUtils.format(ultimoDia, 'dd/MM', locale),
  });
}

export function Cabecalho({
  mes,
  onMudarMes,
  escopo,
  onMudarEscopo,
  ultimoDia,
  estreito = false,
}: Props) {
  const { t } = useTranslation();
  const locale = useLocale();

  return (
    <View
      style={{
        flexDirection: estreito ? 'column' : 'row',
        alignItems: estreito ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: estreito ? 10 : 0,
      }}
    >
      {/* Seletor de mês em "pílula" — a assinatura do topo das telas do
          MULTICAP: fundo branco, borda finíssima, setas nas pontas e o mês
          no meio, em vez de três controles soltos na linha. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          backgroundColor: cor.cartao,
          border: `1px solid ${cor.borda}`,
          borderRadius: 999,
          paddingLeft: 6,
          paddingRight: 6,
        }}
      >
        <Button
          variant="bare"
          aria-label={t('Mês anterior')}
          onPress={() => onMudarMes(monthUtils.prevMonth(mes))}
        >
          ◀
        </Button>
        <Text
          style={{
            ...texto.corpo,
            fontWeight: 600,
            minWidth: 130,
            textAlign: 'center',
            textTransform: 'capitalize',
          }}
        >
          {monthUtils.format(mes, 'MMMM yyyy', locale)}
        </Text>
        <Button
          variant="bare"
          aria-label={t('Próximo mês')}
          onPress={() => onMudarMes(monthUtils.nextMonth(mes))}
        >
          ▶
        </Button>
      </View>

      <Select<Escopo>
        value={escopo}
        onChange={onMudarEscopo}
        options={[
          ['pessoal', t('Pessoal')],
          ['empresa', t('Empresa')],
          ['tudo', t('Tudo')],
        ]}
      />

      <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
        {textoDoLancamento(t, ultimoDia, locale)}
      </Text>
    </View>
  );
}
